import React, { FC, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import toast from "react-hot-toast";
import { Box, IconButton, Menu, MenuItem, Tooltip } from "@mui/material";
import { ReactComponent as MoreOptionsIcon } from "../../../assets/svg/more-options.svg";
import { BriefDialog, BriefReviewDialog } from "../../../components/Brief";
import { ShippingDialog } from "./ShippingDialog";
import { ProductTestingSubmissionsModal } from "../../../components/ProductTesting/ProductTestingSubmissionsModal";
import { BrandProject } from "./constants";
import { PostSuccessDialog, PostSuccessDialogProps } from "./PostSuccessDialog";
import { IconCalendar, IconCircledCheck, IconCircledCross } from "../../../utils/icons";
import ProjectStatusBtn from "./ProjectStatusBtn";
import { FilmingScheduling } from "../../../components/FilmingScheduling";
import ProjectStatusChip from "./ProjectStatusChip";
import { ReactComponent as ClockIcon } from "../../../assets/svg/clock.svg";
import axiosInstant from "../../../api";
import { Button } from "../../../components/Button/Button";
import {
    BRAND_PROJECT_BRIEF_STATUSES,
    BRAND_PROJECT_STATUSES,
    BRAND_PROJECT_STATUS_STYLES,
    getSpecialStatus,
    canAccessStatus,
    getStatusAccessErrorMessage,
    isStatusClickable,
    canReviewBrief,
    canReviewExperts,
    filterRealFiles
} from "../helper";
import { deleteBrandProject, projectDeleteImpact, createShortlist, addExpertToShortlist, importProjectVideos, setProjectVideosApproval } from "../../../api/endpoints";
import AddProjectModal from "./AddProjectModal";
import { ProjectApprovalModal } from "./ProjectApprovalModal";
import { CancelProjectDialog } from "./CancelProjectDialog";
import { CancelledProjectDialog } from "./CancelledProjectDialog";
import {
    canApproveProject,
    cancelBlockedReason,
    briefBlockedReason,
    restoreBlockedReason
} from "./projectStatusActions";
import { useUser } from "../../../hooks/useUser";
import { isAM, isBrand, isInternal } from "../../../utils";
import { ConfirmationDialog } from "../../../components/ConfirmationDialog/ConfirmationDialog";
import { ExpertDenialDialog } from "../../../components/ConfirmationDialog/ExpertDenialDialog";
import ExpertReviewModal from "../expertSelection/ExpertReviewModal";
import { downloadBriefPDF } from "../../../utils/briefPdfGenerator";
import ProjectStatusIcons from "./ProjectStatusIcons";
import { CommentsMenu } from "../../../components/CommentsMenu";
import { PublicUrlMenu, buildPublicUrlOptions } from "../../../components/PublicUrlMenu";
import {
    CommentsFormat,
    downloadProjectComments,
    fetchProjectCommentsById,
} from "../../../utils/commentsExport";
import { setVideosApproval } from "../../../utils/bulkVideoApproval";
import { usePortalPath } from '../../../hooks/usePortalPath'
import { pdpImpactWarning, softDeleteErrorMessage, useDeleteImpact } from "./projectSoftDelete";
import { DeliverablesTrackerModal, TrackerScope } from "../../../components/DeliverablesTracker";

interface IProjectItemMenu {
    project: BrandProject;
    fetchProjects: () => void;
    onUpdate?: (updatedProject?: BrandProject) => void;
    hasExperts?: boolean;
}

export const ProjectItemMenu: FC<IProjectItemMenu> = ({ project, fetchProjects, onUpdate, hasExperts }) => {
    const { briefs } = project;
    const [searchParams, setSearchParams] = useSearchParams();
    const currentStatus = getSpecialStatus(project);
    const { role, user } = useUser();

    const capitalizeWords = (str: string) =>
        str
            .split(" ")
            .map((word) => word.charAt(0).toUpperCase() + word.slice(1))
            .join(" ");
    // A project without a status (the public-share stub an expired session
    // used to receive, or any partial shape) must not throw here: an uncaught
    // render error is reported as a 404 page by the route error boundary.
    const capitalizedStatus = capitalizeWords(currentStatus || "");

    const [showBriefDialog, setShowBriefDialog] = useState(false);
    const [showBriefReviewDialog, setShowBriefReviewDialog] = useState(false);
    const [showShippingDialog, setShowShippingDialog] = useState(false);
    const [showProductTestingDialog, setShowProductTestingDialog] = useState(false);
    const [showPostSuccessDialog, setShowPostSuccessDialog] = useState<Omit<PostSuccessDialogProps, "onClose">>();
    const [showSchedulingPopup, setShowSchedulingPopup] = useState(false);
    const [showDeleteConfirmation, setShowDeleteConfirmation] = useState(false);
    // A second Confirm click while the DELETE is in flight is ignored (the ref
    // catches a double click before the disabled button re-renders).
    const [deleting, setDeleting] = useState(false);
    const deletingRef = useRef(false);
    const deleteImpact = useDeleteImpact(
        showDeleteConfirmation && project.id ? projectDeleteImpact(project.id) : null
    );
    const pdpWarning = pdpImpactWarning(deleteImpact, "project");
    const [showExpertDenialDialog, setShowExpertDenialDialog] = useState(false);
    const [showExpertReviewModal, setShowExpertReviewModal] = useState(false);
    const [isDownloadingBrief, setIsDownloadingBrief] = useState(false);
    const [isDownloadingComments, setIsDownloadingComments] = useState(false);
    const [showImportConfirm, setShowImportConfirm] = useState(false);
    const [importProjectId, setImportProjectId] = useState<any>(null);
    const [showApprovalModal, setShowApprovalModal] = useState(false);
    const [showCancelDialog, setShowCancelDialog] = useState(false);
    const [showCancelledDialog, setShowCancelledDialog] = useState(false);

    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
    const open = Boolean(anchorEl);
    const navigate = useNavigate();
  const { p } = usePortalPath();

    const realFiles = filterRealFiles(project.projectFiles);
    const hasVideos = realFiles.length > 0;
    const allVideosApproved = hasVideos && realFiles.every((f) => f.final === true);
    const anyVideoApproved = realFiles.some((f) => f.final === true);
    const masterVideo = project.projectFiles?.find((f) => f.master);

    const handleClose = () => setAnchorEl(null);
    const [trackerScope, setTrackerScope] = useState<TrackerScope | null>(null);

    const menuClick = (ev: any) => setAnchorEl(ev.currentTarget);

    const handleSetAllVideosApproval = (approve: boolean) => {
        handleClose();
        const brandId = project.brand_id || project.brands?.[0]?.id;
        return setVideosApproval({
            url: brandId ? setProjectVideosApproval(brandId, project.id) : null,
            approve,
            onDone: fetchProjects,
        });
    };

    const handleBriefClick = () => {
        // Guards the menu item AND the brief status icon (onBriefClick), which both
        // land here — submitting a brief un-cancels the project server-side.
        if (briefBlocked) {
            toast.error(briefBlocked);
            handleClose();
            return;
        }
        setShowBriefDialog((prev) => !prev);
        handleClose();
    };

    const openBriefReviewDialog = () => {
        if (canReviewBrief(String(role))) {
            setShowBriefReviewDialog((prev) => !prev);
            handleClose();
        } else {
            toast.success(
                "The brief is being reviewed by your account manager. You will be notified once it is approved."
            );
        }
    };

    const openSchedulingPopup = () => {
        setShowSchedulingPopup((prev) => !prev);
        handleClose();
    };

    const briefTitle = `${briefs?.[0]?.id ? "Edit" : "Create"} Brief`;

    const handleExpertSelection = async () => {
        if (isInternal(role)) {
            try {
                const response = await axiosInstant.get(`/brand-projects/${project.id}`);

                const brief = response.data.briefs?.[0];
                const subProfessionId = brief?.sub_profession_id;

                let url = `/video-production/projects/${project.id}?tab=expertProposals`;

                if (brief?.expert_gender) {
                    url += `&gender=${brief.expert_gender}`;
                }

                if (brief?.expert_age_min !== undefined && brief?.expert_age_max !== undefined) {
                    url += `&agerange=${brief.expert_age_min},${brief.expert_age_max}`;
                }

                if (brief?.profession_id) {
                    url += `&profession=${brief.profession_id}`;
                }

                if (subProfessionId) {
                    url += `&subprofession=${subProfessionId}`;
                }

                navigate(url);
            } catch (error) {
                console.error("Error fetching project details:", error);
                navigate(p(`/video-production/projects/${project.id}?tab=expertProposals`));
            }
        } else {
            toast.success(
                "Experts are being prepared by our expert team and your account manager. You will be notified once they are ready."
            );
        }
        handleClose();
    };

    const handleReviewExpert = async () => {
        if (canReviewExperts(String(role))) {
            setShowExpertReviewModal(true);
            handleClose();
        } else {
            toast.success(
                "The experts are being reviewed by your account manager. You will be notified once they are ready."
            );
        }
    };

    const handleStatusBtnClick = () => {
        // Check if user can access this status
        if (!canAccessStatus(project, currentStatus, role || "", user)) {
            toast.error(getStatusAccessErrorMessage(currentStatus));
            return;
        }

        switch (currentStatus) {
            case BRAND_PROJECT_STATUSES.PENDING:
                // Internal users decide here instead of hand-setting the status
                // in the edit modal; brand-side users keep the edit behaviour.
                if (canApproveProject(project, role)) {
                    setShowApprovalModal(true);
                } else {
                    handleEditProject();
                }
                break;

            case BRAND_PROJECT_STATUSES.BRIEF_PENDING:
                handleBriefClick();
                break;

            case BRAND_PROJECT_STATUSES.BRIEF_REVIEW_NEEDED:
                openBriefReviewDialog();
                break;

            case BRAND_PROJECT_STATUSES.EXPERT_PROPOSALS_NEEDED:
                handleExpertSelection();
                break;

            case BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED:
                handleReviewExpert();
                break;

            case BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED:
                setShowExpertDenialDialog(true);
                break;

            case BRAND_PROJECT_STATUSES.BRIEF_DENIED:
                setShowPostSuccessDialog({
                    icon: <IconCircledCross />,
                    title: BRAND_PROJECT_STATUSES.BRIEF_DENIED,
                    text: (
                        <>
                            <b>Reason:</b> {project.briefs?.[0]?.denied_reason}
                        </>
                    ),
                    actions: [
                        <Button
                            className="!text-sm !font-normal !min-w-min !px-5 !py-2"
                            onClick={() => {
                                setShowPostSuccessDialog(undefined);
                                setShowBriefDialog(true);
                            }}
                        >
                            Edit Brief
                        </Button>,
                        <Button
                            color="default"
                            variant="soft"
                            className="!text-sm !font-normal !min-w-min !px-5 !py-2"
                            onClick={() => setShowPostSuccessDialog(undefined)}
                        >
                            Close
                        </Button>
                    ]
                });
                break;

            case BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED:
                setSearchParams((prev: any) => {
                    prev.set("expertSelection", project.id);
                    return prev;
                });
                break;

            case BRAND_PROJECT_STATUSES.SCHEDULING:
                setShowSchedulingPopup(true);
                break;

            case BRAND_PROJECT_STATUSES.PRE_PRODUCTION:
                openSchedulingPopup();
                break;

            // Editing: feedback is in and post is on the next version. The
            // review page stays open so the client can re-watch the version they
            // commented on (read-only — see VideoFeedbackProvider isReviewLocked).
            case BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED:
            case BRAND_PROJECT_STATUSES.EDITING:
                navigate(p(`/video-production/projects/${project.id}?tab=video`));
                break;

            // Reason + Restore Project (the dashboard's getStatusAction routes
            // the same status to the same dialog).
            case BRAND_PROJECT_STATUSES.CANCELLED:
                setShowCancelledDialog(true);
                break;

            default:
                break;
        }
    };

    const handleDeleteProject = () => {
        setShowDeleteConfirmation(true);
        handleClose();
    };

    const handleCancelProject = () => {
        setShowCancelDialog(true);
        handleClose();
    };

    /**
     * Refresh the whole list after a status change — approving can move sibling
     * projects on the same selection, so refreshing this row alone is not enough.
     *
     * onUpdate is preferred and fetchProjects is only the fallback: on the projects
     * list ProjectListItem passes the SAME function as both props, so calling both
     * runs the loader twice concurrently; and on the expert-selection page
     * fetchProjects is window.location.reload(), which would tear the page down on
     * top of the success toast. Every current caller supplies onUpdate.
     */
    const refreshAfterStatusChange = () => {
        if (onUpdate) {
            onUpdate();
        } else {
            fetchProjects();
        }
    };

    const handleProjectImport = (id: any) => {
        setImportProjectId(id);
        setShowImportConfirm(true);
    };

    const confirmProjectImport = async () => {
        setShowImportConfirm(false);
        if (importProjectId) {
            const endpoint = importProjectVideos(importProjectId);
            try {
                await axiosInstant.get(endpoint);
                toast.success("Import finished.");
            } catch (error) {
                toast.error((error as Error).message);
            }
        }
        setImportProjectId(null);
    };

    const confirmDeleteProject = async () => {
        if (!project.id || deletingRef.current) return;
        deletingRef.current = true;
        setDeleting(true);
        try {
            await axiosInstant.delete(deleteBrandProject(project.id));
            toast.success("Project deleted. Restore it from the Deleted tab on the Projects page.");
            onUpdate?.();
        } catch (error) {
            console.error("Failed to delete project:", error);
            toast.error(softDeleteErrorMessage(error, "Failed to delete project. Please try again."));
        } finally {
            deletingRef.current = false;
            setDeleting(false);
            setShowDeleteConfirmation(false);
        }
    };

    const getStatusProp = (status: string, prop: string): string | null => {
        const statusKey = Object.keys(BRAND_PROJECT_STATUSES).find((key) => BRAND_PROJECT_STATUSES[key] === status);
        return statusKey ? BRAND_PROJECT_STATUS_STYLES[statusKey][prop] : null;
    };

    // Public order/project page links (TDC-1724): copied from the link
    // icon, opened from the ⋯ menu. Each follows its own page's toggle.
    const publicUrlOptions = buildPublicUrlOptions(project, isInternal(role) || isBrand(role));

    const handleEditProject = () => {
        if (project.id) {
            setSearchParams(
                (params) => {
                    params.set("edit", String(project.id));
                    return params;
                },
                { replace: true }
            );
        }
        handleClose();
    };

    const handleCreateShortlist = async () => {
        try {
            const projectResponse = await axiosInstant.get(`/brand-projects/${project.id}`);
            const fullProjectData = projectResponse.data;

            if (!fullProjectData.project_pros || fullProjectData.project_pros.length === 0) {
                toast.error("No experts available to add to shortlist");
                return;
            }

            const shortlistName = `${project.title} shortlist`;
            const shortlistResponse = await axiosInstant.post(createShortlist(), {
                name: shortlistName
            });

            const shortlistId = shortlistResponse.data.id;

            const expertPromises = fullProjectData.project_pros.map((expert: any) =>
                axiosInstant.post(addExpertToShortlist(shortlistId), {
                    userId: expert.userid
                })
            );

            await Promise.all(expertPromises);

            toast.success(`${shortlistName} created successfully with ${fullProjectData.project_pros.length} experts!`);
            handleClose();
        } catch (error: any) {
            if (error?.response?.data === "NameExists") {
                toast.error("Shortlist with this name already exists!");
            } else {
                toast.error("Failed to create shortlist");
            }
        }
    };

    const handleDownloadComments = async (format: CommentsFormat) => {
        if (isDownloadingComments) return;
        setIsDownloadingComments(true);
        const toastId = toast.loading("Preparing comments...");
        try {
            const comments = await fetchProjectCommentsById(
                Number(project.id),
                project.title,
            );
            const sent = await downloadProjectComments(comments, format);
            if (!sent) {
                toast.error("No comments have been left on this project.", { id: toastId });
                return;
            }
            toast.success("Comments downloaded.", { id: toastId });
        } catch (error: any) {
            toast.error(
                error?.response?.data?.error ?? "Failed to download comments.",
                { id: toastId },
            );
        } finally {
            setIsDownloadingComments(false);
        }
    };

    const handleDownloadBrief = async () => {
        if (isDownloadingBrief) return;

        const brief = project.briefs?.[0];
        if (!brief) {
            toast.error("This project has no brief to download.");
            handleClose();
            return;
        }

        setIsDownloadingBrief(true);
        const toastId = toast.loading("Preparing brief...");
        try {
            await downloadBriefPDF(brief, project.title, undefined);
            toast.success("Brief downloaded.", { id: toastId });
        } catch (error) {
            toast.error("Failed to download the brief. Please try again.", { id: toastId });
        } finally {
            setIsDownloadingBrief(false);
            handleClose();
        }
    };

    const handleViewProject = () => {
        handleBriefClick();
    };

    const statusClickable = isStatusClickable(project, currentStatus, role || "", user);
    const cancelBlocked = cancelBlockedReason(project);
    const isCancelled = project.status === BRAND_PROJECT_STATUSES.CANCELLED;
    const restoreBlocked = restoreBlockedReason(project);
    const briefBlocked = briefBlockedReason(project);

    const handleExpertIconClick = () => {
        switch (currentStatus) {
            case BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED:
                handleReviewExpert();
                break;
            case BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED:
                setShowExpertDenialDialog(true);
                break;
            case BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED:
                setSearchParams((prev: any) => {
                    prev.set("expertSelection", project.id);
                    return prev;
                });
                break;
            case BRAND_PROJECT_STATUSES.SCHEDULING:
                setShowSchedulingPopup(true);
                break;
            default:
                handleExpertSelection();
        }
    };

    const handleReviewIconClick = () => {
        navigate(p(`/video-production/projects/${project.id}?tab=video`));
    };

    return (
        <>
            {currentStatus === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_DENIED ||
            currentStatus === BRAND_PROJECT_STATUSES.BRIEF_DENIED ? (
                <ProjectStatusBtn
                    background="#FFBC00"
                    text={capitalizedStatus}
                    textColor="#8F6C32"
                    icon={<IconCalendar />}
                    onClick={handleStatusBtnClick}
                />
            ) : (
                <a
                    onClick={handleStatusBtnClick}
                    style={{
                        cursor: statusClickable ? "pointer" : "not-allowed",
                        opacity: statusClickable ? 1 : 0.5
                    }}
                >
                    <ProjectStatusChip
                        background={getStatusProp(currentStatus, "color") || "var(--tint)"}
                        text={capitalizedStatus}
                        textColor={getStatusProp(currentStatus, "textColor") || "var(--accent-text)"}
                        icon={getStatusProp(currentStatus, "icon") || <ClockIcon className="w-4 h-4" />}
                    />
                </a>
            )}
            <Box
                sx={{
                    display: "flex",
                    alignItems: "center",
                    gap: 0,
                    flexShrink: 0,
                    "& .MuiIconButton-root": { padding: "8px" }
                }}
            >
                <ProjectStatusIcons
                    project={project}
                    role={role || ""}
                    user={user}
                    onBriefClick={handleViewProject}
                    onExpertClick={handleExpertIconClick}
                    onShippingClick={() => setShowShippingDialog(true)}
                    onProductTestingClick={() => setShowProductTestingDialog(true)}
                    onReviewClick={handleReviewIconClick}
                />

                {(project as any)?.comment_count !== 0 && (
                    <CommentsMenu
                        variant="icon"
                        glyphSize={20}
                        busy={isDownloadingComments}
                        onSelect={handleDownloadComments}
                    />
                )}

                <PublicUrlMenu
                    variant="icon"
                    glyphSize={20}
                    options={publicUrlOptions}
                />

                <IconButton id="basic-button" onClick={menuClick}>
                    <MoreOptionsIcon />
                </IconButton>
            </Box>
            <Menu
                id="basic-menu"
                anchorEl={anchorEl}
                open={open}
                onClose={handleClose}
                MenuListProps={{
                    "aria-labelledby": "basic-button"
                }}
            >
                <MenuItem onClick={handleEditProject}>Edit Project</MenuItem>

                {/* aria-disabled for the same reason as Cancel Project below: a real
                    `disabled` would hide the tooltip explaining why. */}
                <Tooltip title={briefBlocked || ""} placement="left">
                    <MenuItem
                        onClick={handleBriefClick}
                        aria-disabled={!!briefBlocked}
                        sx={{ opacity: briefBlocked ? 0.5 : 1 }}
                    >
                        {briefTitle}
                    </MenuItem>
                </Tooltip>

                {isInternal(role) &&
                    briefs?.[0] &&
                    briefs?.[0]?.status !== BRAND_PROJECT_BRIEF_STATUSES.APPROVED &&
                    briefs?.[0]?.status !== BRAND_PROJECT_BRIEF_STATUSES.DENIED && (
                        <MenuItem onClick={openBriefReviewDialog}>Review Brief</MenuItem>
                    )}
                <MenuItem onClick={handleDownloadBrief} disabled={isDownloadingBrief}>
                    {isDownloadingBrief ? "Downloading..." : "Download Brief"}
                </MenuItem>
                <MenuItem
                    onClick={() => {
                        handleClose();
                        setTrackerScope({ projectId: project.id });
                    }}
                >
                    Deliverables tracker
                </MenuItem>
                {isInternal(role) && <MenuItem onClick={handleExpertSelection}>Propose Experts</MenuItem>}

                {isInternal(role) && currentStatus === BRAND_PROJECT_STATUSES.EXPERTS_REVIEW_NEEDED && (
                    <MenuItem onClick={handleStatusBtnClick}>Review Experts</MenuItem>
                )}

                {!isInternal(role) && currentStatus === BRAND_PROJECT_STATUSES.EXPERTS_SELECTION_NEEDED && (
                    <MenuItem onClick={handleStatusBtnClick}>Select Experts</MenuItem>
                )}

                {isInternal(role) && currentStatus === BRAND_PROJECT_STATUSES.SCHEDULING && (
                    <MenuItem onClick={handleStatusBtnClick}>Set Film Date</MenuItem>
                )}

                {(currentStatus === BRAND_PROJECT_STATUSES.VERSION_REVIEW_NEEDED ||
                    currentStatus === BRAND_PROJECT_STATUSES.EDITING) && (
                    <MenuItem onClick={handleStatusBtnClick}>Review Video</MenuItem>
                )}

                {isInternal(role) && project.id && (
                    <MenuItem
                        onClick={handleCreateShortlist}
                        disabled={hasExperts !== undefined ? !hasExperts : !project.project_pros?.length}
                    >
                        Create Shortlist
                    </MenuItem>
                )}

                {publicUrlOptions
                    .filter((option) => !!option.url)
                    .map((option) => (
                        <MenuItem
                            key={`open-${option.key}`}
                            onClick={() => {
                                window.open(option.url as string, "_blank");
                                handleClose();
                            }}
                        >
                            Open {option.title}
                        </MenuItem>
                    ))}

                {hasVideos && (
                    <MenuItem onClick={() => handleSetAllVideosApproval(true)} disabled={allVideosApproved}>
                        Approve All Videos
                    </MenuItem>
                )}

                {hasVideos && (
                    <MenuItem onClick={() => handleSetAllVideosApproval(false)} disabled={!anyVideoApproved}>
                        Unapprove All Videos
                    </MenuItem>
                )}

                {/* aria-disabled, NOT disabled. Two MUI constraints meet here:
                    ButtonBase sets pointer-events:none only on `.Mui-disabled`, so a
                    real `disabled` would swallow the hover and the reason could never
                    be read; and MenuList moves focus with nextElementSibling without
                    descending into children, so wrapping the item in a <span> to dodge
                    that would drop it out of arrow-key/Home/End/typeahead navigation
                    entirely. Keeping the MenuItem a direct, non-disabled child of the
                    Menu satisfies both — it stays reachable, and clicking it explains
                    itself instead of doing nothing. */}
                {isInternal(role) && !isCancelled && (
                    <Tooltip title={cancelBlocked || ""} placement="left">
                        <MenuItem
                            aria-disabled={!!cancelBlocked}
                            onClick={
                                cancelBlocked
                                    ? () => {
                                          toast.error(cancelBlocked);
                                          handleClose();
                                      }
                                    : handleCancelProject
                            }
                            sx={{ opacity: cancelBlocked ? 0.5 : 1 }}
                        >
                            Cancel Project
                        </MenuItem>
                    </Tooltip>
                )}

                {/* The cancel item's counterpart: a cancelled project offers Restore
                    instead. Opens the same dialog the status chip does, which
                    shows the reason and confirms before restoring. */}
                {isInternal(role) && isCancelled && (
                    <Tooltip title={restoreBlocked || ""} placement="left">
                        <MenuItem
                            aria-disabled={!!restoreBlocked}
                            onClick={() => {
                                setShowCancelledDialog(true);
                                handleClose();
                            }}
                            sx={{ opacity: restoreBlocked ? 0.5 : 1 }}
                        >
                            Restore Project
                        </MenuItem>
                    </Tooltip>
                )}

                {isAM(role) && (
                    <>
                        <MenuItem onClick={handleDeleteProject}>Delete Project</MenuItem>
                        <MenuItem onClick={() => handleProjectImport(project.id)}>Video Re-Import</MenuItem>
                    </>
                )}
            </Menu>

            <DeliverablesTrackerModal scope={trackerScope} onClose={() => setTrackerScope(null)} />

            {showBriefDialog && (
                <BriefDialog
                    project={project}
                    onClose={() => setShowBriefDialog(false)}
                    onComplete={(updatedProject) => {
                        setShowBriefDialog(false);
                        if (updatedProject) {
                            onUpdate?.(updatedProject);
                        } else {
                            onUpdate?.();
                        }
                    }}
                />
            )}

            {showBriefReviewDialog && (
                <BriefReviewDialog
                    project={project}
                    onClose={() => setShowBriefReviewDialog(false)}
                    onComplete={() => {
                        setShowBriefReviewDialog(false);
                        onUpdate?.();
                    }}
                />
            )}

            {showApprovalModal && (
                <ProjectApprovalModal
                    project={project}
                    onClose={() => setShowApprovalModal(false)}
                    onComplete={() => {
                        setShowApprovalModal(false);
                        refreshAfterStatusChange();
                    }}
                />
            )}

            {showCancelDialog && (
                <CancelProjectDialog
                    project={project}
                    onClose={() => setShowCancelDialog(false)}
                    onComplete={() => {
                        setShowCancelDialog(false);
                        refreshAfterStatusChange();
                    }}
                />
            )}

            {showCancelledDialog && (
                <CancelledProjectDialog
                    project={project}
                    onClose={() => setShowCancelledDialog(false)}
                    onRestored={() => {
                        setShowCancelledDialog(false);
                        refreshAfterStatusChange();
                    }}
                />
            )}

            {showShippingDialog && (
                <ShippingDialog
                    project={project}
                    onClose={() => setShowShippingDialog(false)}
                    onComplete={() => {
                        setShowShippingDialog(false);
                        setShowPostSuccessDialog({
                            icon: <IconCircledCheck />,
                            title: "Saved",
                            text: "Thank you for saving your shipping information"
                        });
                    }}
                />
            )}

            {showProductTestingDialog && project?.id && (
                <ProductTestingSubmissionsModal
                    projectId={project.id}
                    onClose={() => setShowProductTestingDialog(false)}
                />
            )}

            {!!showPostSuccessDialog && (
                <PostSuccessDialog
                    {...showPostSuccessDialog}
                    onClose={() => {
                        setShowPostSuccessDialog(undefined);
                        onUpdate?.();
                    }}
                />
            )}

            {showSchedulingPopup && (
                <FilmingScheduling
                    project={project}
                    onClose={() => setShowSchedulingPopup(false)}
                    onComplete={() => {
                        setShowSchedulingPopup(false);
                        // A save can change dates/status on EVERY project of the
                        // selection (reschedule → back to Scheduling), so refresh
                        // the list like the other dialogs do, not just this row.
                        refreshAfterStatusChange();
                    }}
                />
            )}

            <ConfirmationDialog
                open={showDeleteConfirmation}
                onClose={() => setShowDeleteConfirmation(false)}
                onConfirm={confirmDeleteProject}
                disabled={deleting}
                title="Delete Project"
                description={[
                    `Delete "${project.title || "this project"}"? Its videos are removed with it.`,
                    ...(pdpWarning ? [pdpWarning] : []),
                    "This is a soft delete: an admin can restore the project and its videos from the Deleted tab on the Projects page."
                ]}
                confirmLabel="Delete"
                cancelLabel="Cancel"
            />

            <ConfirmationDialog
                open={showImportConfirm}
                onClose={() => {
                    setShowImportConfirm(false);
                    setImportProjectId(null);
                }}
                onConfirm={confirmProjectImport}
                title="Project Video Re-Import"
                description="Are you sure you want to import all videos for this project?"
                confirmLabel="Import"
                cancelLabel="Cancel"
            />

            <ExpertDenialDialog
                open={showExpertDenialDialog}
                onClose={() => setShowExpertDenialDialog(false)}
                onReviewExperts={() => {
                    setShowExpertDenialDialog(false);
                    handleExpertSelection();
                }}
                denialReason={project.briefs?.[0]?.denied_reason || "No reason provided"}
            />

            {showExpertReviewModal && (
                <ExpertReviewModal
                    open={showExpertReviewModal}
                    onClose={() => setShowExpertReviewModal(false)}
                    projectId={project.id}
                    onConfirm={() => {
                        setShowExpertReviewModal(false);
                        onUpdate?.();
                    }}
                />
            )}
        </>
    );
};
